/**
 * 語言相關的工具函數
 */

/**
 * 支援的語言列表和對應關係
 */
// Synced with config/locales/ — only the 5 supported UI languages
const LANGUAGE_MAP = {
  'zh-TW': 'zh-TW',
  'zh-CN': 'zh-CN',
  'en': 'en',
  'ja': 'ja',
  'ko': 'ko'
};

/**
 * 支援的語言代碼列表（內部格式）
 */
export const SUPPORTED_LANGUAGES = Object.keys(LANGUAGE_MAP);

/**
 * 支援的URL語言代碼列表
 */
export const SUPPORTED_URL_LANGUAGES = Object.values(LANGUAGE_MAP);

/**
 * 預設語言
 */
export const DEFAULT_LANGUAGE = 'zh-TW';
export const DEFAULT_URL_LANGUAGE = 'zh-TW';

/**
 * 從URL參數獲取當前語言設定
 * @param {string} defaultLanguage - 預設語言
 * @returns {string} 語言代碼
 */
export const getCurrentLocale = (defaultLanguage = DEFAULT_LANGUAGE) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('locale') || defaultLanguage;
};

/**
 * 將內部語言代碼轉換為URL格式
 * @param {string} language - 內部語言代碼（如 zh_TW）
 * @returns {string} URL格式的語言代碼（如 zh-tw）
 */
export const getUrlLanguageCode = (language) => {
  return LANGUAGE_MAP[language] || language;
};

/**
 * 將URL格式的語言代碼轉換為內部格式
 * @param {string} urlCode - URL格式的語言代碼（如 zh-tw）
 * @returns {string} 內部格式的語言代碼（如 zh_TW）
 */
export const getInternalLanguageCode = (urlCode) => {
  const entry = Object.entries(LANGUAGE_MAP).find(([, url]) => url === urlCode);
  return entry ? entry[0] : urlCode;
};

/**
 * 驗證語言代碼是否有效（內部格式）
 * @param {string} language - 語言代碼
 * @returns {boolean} 是否有效
 */
export const isValidLanguage = (language) => {
  return SUPPORTED_LANGUAGES.includes(language);
};

/**
 * 驗證URL語言代碼是否有效
 * @param {string} urlLanguage - URL格式的語言代碼
 * @returns {boolean} 是否有效
 */
export const isValidUrlLanguage = (urlLanguage) => {
  return SUPPORTED_URL_LANGUAGES.includes(urlLanguage);
};

/**
 * 獲取支援的語言列表
 * @returns {Array} 語言代碼陣列
 */
export const getSupportedLanguages = () => {
  return [...SUPPORTED_LANGUAGES];
};

/**
 * 獲取支援的URL語言列表
 * @returns {Array} URL語言代碼陣列  
 */
export const getSupportedUrlLanguages = () => {
  return [...SUPPORTED_URL_LANGUAGES];
};

/**
 * 獲取語言的顯示名稱（需要配合翻譯系統使用）
 * @param {string} language - 語言代碼
 * @param {Object} translations - 翻譯對象
 * @returns {string} 語言顯示名稱
 */
export const getLanguageDisplayName = (language, translations = {}) => {
  const languageKey = language.replace('_', '-');
  return translations?.languages?.[languageKey] || language;
};

/**
 * 根據瀏覽器語言設定獲取最佳匹配語言
 * @param {Array} availableLanguages - 可用語言列表
 * @returns {string} 最佳匹配的語言代碼
 */
export const getBrowserPreferredLanguage = (availableLanguages = SUPPORTED_LANGUAGES) => {
  const browserLangs = navigator.languages || [navigator.language];
  
  for (const browserLang of browserLangs) {
    // 精確匹配
    const exactMatch = availableLanguages.find(lang => 
      getUrlLanguageCode(lang) === browserLang.toLowerCase()
    );
    if (exactMatch) return exactMatch;
    
    // 語言族群匹配（如 zh-CN 匹配 zh）
    const langFamily = browserLang.split('-')[0];
    const familyMatch = availableLanguages.find(lang =>
      getUrlLanguageCode(lang).split('-')[0] === langFamily
    );
    if (familyMatch) return familyMatch;
  }
  
  return DEFAULT_LANGUAGE;
};

/**
 * 檢查是否為RTL（從右到左）語言
 * @param {string} language - 語言代碼
 * @returns {boolean} 是否為RTL語言
 */
export const isRtlLanguage = (language) => {
  const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
  const urlCode = getUrlLanguageCode(language);
  return rtlLanguages.includes(urlCode);
};